import { NewHire, OnboardingDoc, OnboardingHireStep } from '../../models';
import { NEW_HIRES, ONBOARDING_DOCS } from '../prototype/onboarding-data';
import { DEFAULT_ONBOARDING_TEMPLATES } from '../prototype/candidates-data';

/** Snapshot the default post-hire bucket steps onto a hire (unified process). */
const DEFAULT_HIRE_STEPS: OnboardingHireStep[] = DEFAULT_ONBOARDING_TEMPLATES.buckets.flatMap((b) =>
  b.items.map((i) => ({
    id: i.id,
    label: i.label,
    bucketId: b.id,
    bucketLabel: b.label,
    assignee: i.assignee,
  })),
);

/** Required onboarding documents — from the prototype ONBOARDING_DOCS. */
export const ONBOARDING_DOCS_SEED: readonly OnboardingDoc[] = (ONBOARDING_DOCS as any[]).map(
  (d): OnboardingDoc => ({
    id: d.id,
    label: d.label ?? '',
    appliesTo: d.appliesTo ?? 'all',
    requiresParentIfMinor: !!d.requiresParentIfMinor,
    // Everything is signed except the direct-deposit form, which is provided/uploaded.
    signatureRequired: d.signatureRequired ?? d.id !== 'direct-dep',
    // The legacy provide-only shape (signatureRequired false) has always meant
    // "hand this in", so it carries the upload requirement explicitly now.
    uploadRequired: d.uploadRequired ?? (d.signatureRequired ?? d.id !== 'direct-dep') === false,
    legalBasis: d.legalBasis ?? '',
    body: d.body ?? undefined,
  }),
);

/** New hires in the onboarding pipeline — from the prototype NEW_HIRES. */
export const NEW_HIRES_SEED: readonly NewHire[] = (NEW_HIRES as any[]).map(
  (h): NewHire => ({
    id: h.id,
    name: h.name ?? '',
    title: h.title ?? '',
    positionId: h.positionId,
    opco: h.opco,
    function: h.function,
    tier: h.tier,
    reportsToName: h.reportsToName,
    initials: h.initials ?? '',
    age: h.age,
    isMinor: !!h.isMinor,
    parentName: h.parentName,
    parentEmail: h.parentEmail,
    startDate: h.startDate,
    signatures: h.signatures ?? {},
    onboardingDone: h.onboardingDone ?? {},
    // Unified process: seeded hires carry the default post-hire steps so their
    // My Onboarding + the Supervisor View read one shared checklist.
    steps: h.steps ?? DEFAULT_HIRE_STEPS.map((s) => ({ ...s })),
    stepRecords: h.stepRecords ?? {},
  }),
);

/** Whether a document applies to a given hire. */
export function docApplies(doc: OnboardingDoc, hire: NewHire): boolean {
  if (doc.appliesTo === 'all') return true;
  if (doc.appliesTo === 'field') return hire.tier === 'Field' || hire.tier === 'Trade';
  if (doc.appliesTo === 'minor-only') return !!hire.age && hire.age < 18;
  return false;
}
